import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { getApiErrorStatus } from '@/utils/apiError'
import type {
  CreateCoverageResponsibleRequest,
  ListCoverageResponsiblesParams,
  UpdateCoverageResponsibleRequest
} from '../Types/Requests/CoverageRequests'
import type { CoverageResponsibleResponse } from '../Types/Responses/CoverageResponses'

/**
 * O backend montou a rota no controller legado de unidades, que é `healthUnits`
 * (camelCase): `/healthUnits/:healthUnitId/coverage-responsibles[/:id]`.
 */
export const COVERAGE_RESPONSIBLES_BASE_PATH = '/healthUnits'

/** 404/405/501 = rota ainda não deployada; o web sobe antes do backend. */
const BACKEND_WITHOUT_ROUTE_STATUSES = [404, 405, 501]

export default class CoverageResponsibleService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = COVERAGE_RESPONSIBLES_BASE_PATH
  }

  private path(healthUnitId: number, suffix = ''): string {
    return this.getCompletePath(`/${healthUnitId}/coverage-responsibles${suffix}`)
  }

  /** `null` quando o backend ainda não tem a rota — a tela esconde a lista. */
  async list(
    healthUnitId: number,
    params: ListCoverageResponsiblesParams = {}
  ): Promise<CoverageResponsibleResponse[] | null> {
    try {
      const { data } = await request.get<CoverageResponsibleResponse[]>(this.path(healthUnitId), {
        includeInactive: params.includeInactive ?? false
      })
      return Array.isArray(data) ? data : []
    } catch (e) {
      if (BACKEND_WITHOUT_ROUTE_STATUSES.includes(getApiErrorStatus(e) ?? 0)) {
        console.debug('[CoverageResponsible] rota indisponível; lista oculta', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível carregar os responsáveis pela cobertura')
    }
  }

  async create(
    healthUnitId: number,
    payload: CreateCoverageResponsibleRequest
  ): Promise<CoverageResponsibleResponse> {
    try {
      const { data } = await request.post<CoverageResponsibleResponse>(
        this.path(healthUnitId),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar o responsável pela cobertura')
    }
  }

  async update(
    healthUnitId: number,
    responsibleId: number,
    payload: UpdateCoverageResponsibleRequest
  ): Promise<CoverageResponsibleResponse> {
    try {
      const { data } = await request.patch<CoverageResponsibleResponse>(
        this.path(healthUnitId, `/${responsibleId}`),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar o responsável pela cobertura')
    }
  }
}
